import { Layout } from 'antd';
import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { DialogHost } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import { AUTH_ERROR_CODES, AUTH_POLICY, useLogout, useSessionKeepAlive } from '@/features/auth';
import { useConnectorFailureToast } from '@/features/device-params';
import { ProfileFlows } from '@/features/profile';
import { useIdleTimeout } from '@/hooks/use-idle-timeout';
import { sessionExpiry } from '@/lib/api/session-expiry';
import { tokenStore } from '@/lib/api/token-store';
import { useAuth } from '@/providers/auth-provider';

import { ModuleHeaderProvider, PageHeader } from '../module-header';
import { PageContainer } from '../page-container';
import { Sidebar } from '../sidebar';

import styles from './app-shell.module.scss';

const { Content } = Layout;

// 117853 §14a
const SESSION_EXPIRED_COPY = 'Your session has expired. Please sign in again.';

export function AppShell() {
  const { status, rememberMe, clearSession } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();

  useIdleTimeout({
    enabled: !rememberMe,
    timeoutMs: AUTH_POLICY.idleMs,
    onIdle: async () => {
      // An offline idle logout must not leave a live access token in memory behind the login
      // page: `logout()` (117853 §13e) only clears the store on a successful server revoke, so
      // a failure here has to clear it itself.
      await logout.mutateAsync().catch(() => tokenStore.clear());
      clearSession();
      navigate(routes.login);
      toast.info({ title: SESSION_EXPIRED_COPY });
    },
  });
  // The proactive half of session survival: the refresh cookie is shorter-lived than the idle
  // timer above, so without this an active admin is silently thrown out mid-session.
  useSessionKeepAlive({ enabled: status === 'authenticated' });

  // `<AuthProvider>` already flips the status (and `<RequireAuth>` the route) when a refresh
  // fails; this only says so out loud, in the same words the idle timeout uses. The listener
  // runs synchronously inside `expire()`, before the redirect unmounts this shell, and antd's
  // notification holder sits above it — so the toast survives the navigation.
  useEffect(
    () =>
      sessionExpiry.subscribe((reason) => {
        // A deactivated account (ADO 118305) gets the backend's own reason; every other cause
        // (an expired refresh cookie, a blip) keeps the generic expiry copy.
        const deactivatedMessage =
          reason && reason.code === AUTH_ERROR_CODES.ACCOUNT_DEACTIVATED
            ? reason.message
            : undefined;
        toast.info({ title: deactivatedMessage ?? SESSION_EXPIRED_COPY });
      }),
    [toast],
  );

  useConnectorFailureToast();

  return (
    <ModuleHeaderProvider>
      <DialogHost>
        <Layout className={styles.shell}>
          <Sidebar />
          <Layout className={styles.main}>
            <Content className={styles.content}>
              <PageContainer>
                <PageHeader />
                <Outlet />
              </PageContainer>
            </Content>
          </Layout>
        </Layout>
        <ProfileFlows />
      </DialogHost>
    </ModuleHeaderProvider>
  );
}
